import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import avatar from "assets/icons/avatar.png";
import closeIcon from "assets/icons/x-circle.svg";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import { useAppSelector } from "store/index";

import styles from "./ProfileModal.module.scss";

type Props = {
  onClose: () => void;
};

const ProfileModal: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const user = useAppSelector((store) => store.auth.info);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const fields = [
    { label: t("id"), value: user?.id, isCopyable: true },
    { label: t("role"), value: user?.roleName },
    { label: t("email"), value: user?.email, isCopyable: true },
    { label: t("currency"), value: user?.currency },
    {
      label: t("locale"),
      value: user?.locale,
      flagCode: user?.locale?.split("-")[1]?.toLowerCase(),
    },
    {
      label: t("country"),
      value: user?.country,
      flagCode: user?.country?.toLowerCase(),
    },
  ];

  return (
    <div className={styles.wrapper} role="presentation" onClick={onClose}>
      <section
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 id="profile-title">{t("profile")}</h2>
          <button type="button" onClick={onClose} aria-label={t("close")}>
            <img src={closeIcon} alt="" />
          </button>
        </header>

        <div className={styles.avatar}>
          <img src={avatar} alt="" />
        </div>

        <div className={styles.fields}>
          {fields.map(({ label, value, isCopyable, flagCode }) => (
            <div className={styles.field} key={label}>
              <span>{label}</span>
              <div className={styles.fieldContent}>
                <div className={styles.value}>
                  {flagCode && (
                    <img
                      className={styles.flag}
                      src={`https://flagpedia.net/data/flags/icon/16x12/${flagCode}.png`}
                      alt=""
                    />
                  )}
                  <strong>{value ?? "-"}</strong>
                </div>
                {isCopyable && value != null && (
                  <CopyToClipboard
                    text={String(value)}
                    notificationMessage=""
                    className={styles.copyButton}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ProfileModal;
